import { Component, useEffect, useState, type ReactNode } from "react";
import { beginSceneLoad, type SceneResult } from "./loadingState";

export type SceneStatus = "loading" | "ready" | "error";
interface BoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  onError?: () => void;
}

class SceneBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onError?.(); }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

interface SceneProps<T> {
  load: () => Promise<T>;
  children: (module: T) => ReactNode;
  fallback: (failed: boolean, retry: () => void) => ReactNode;
  onStatus?: (status: SceneStatus) => void;
}

/** Each retry owns new request/boundary state; a rejected React.lazy is not reused. */
export function RecoverableScene<T>(props: SceneProps<T>) {
  const [attempt, setAttempt] = useState(0);
  return <SceneAttempt key={attempt} {...props} retry={() => setAttempt(value => value + 1)} />;
}

function SceneAttempt<T>({ load, children, fallback, onStatus, retry }: SceneProps<T> & { retry: () => void }) {
  const [result, setResult] = useState<SceneResult<T> | null>(null);
  useEffect(() => {
    onStatus?.("loading");
    return beginSceneLoad(load, next => {
      setResult(next);
      onStatus?.(next.status);
    });
  }, [load, onStatus]);

  if (!result || result.status === "error") return <>{fallback(result?.status === "error", retry)}</>;
  return <SceneBoundary onError={() => onStatus?.("error")} fallback={fallback(true, retry)}>
    {children(result.value)}
  </SceneBoundary>;
}
